import type { ControlConfiguration } from '@src/components/sidepanel/CompositionSettings/types';

/**
 * Namecard-specific helper for the composition `originalValueOverrides` hook.
 *
 * Mirrors the quote-card helpers in `quoteCardsCompositionSettings.ts`: it feeds
 * `applyDefaultControlValuesToSource` so that, at element-creation time, the
 * speaker-bound Name/Title text controls never render the template's baked-in
 * placeholder text. The real values are applied a tick later by the async
 * `resolveAndApplySpeakerForCompositionElement` step.
 */

/** Loading-state text shown for a Name/Title field while speaker data resolves. */
export const NAMECARD_LOADING_PLACEHOLDER = 'Loading…';

/** Resolved speaker values already known when a namecard is created. */
export interface NamecardSpeakerValues {
  name?: string;
  title?: string;
}

/**
 * Compute the original-value override for a single control on a namecard so the
 * speaker-bound Name/Title text controls suppress the template's placeholder.
 *
 * `speakerControlBindings` is the SpeakerControl's `controlBindings` map
 * (`{ [textControlId]: 'name' | 'title' | 'headshot' }`); we match the given
 * control's `id` against it — the inverse of
 * `applySpeakerControlValueMappingToControls`, so we target the same controls
 * the async resolve step will later overwrite.
 *
 * Returns:
 *  - `NAMECARD_LOADING_PLACEHOLDER` when this is the bound Name/Title control and
 *    a real value is coming, so the async resolve replaces "Loading…" with it;
 *  - `''` when the field is bound but empty/missing, so the value is written as
 *    blank (`applyDefaultControlValuesToSource` only skips null/undefined, so ''
 *    is applied) — the template default is never shown and "Loading…" can never
 *    stick on a field the speaker lacks (the async step skips null/undefined);
 *  - `undefined` for non-bound / non-text controls, leaving normal default
 *    precedence (`customValue ?? originalValue`) untouched.
 */
export function getNamecardValueOverrides(
  control: ControlConfiguration,
  speakerValues: NamecardSpeakerValues | null | undefined,
  speakerControlBindings: Record<string, string> | undefined,
): unknown {
  if (!speakerValues || !speakerControlBindings) return undefined;
  if (control.type !== 'text' || !control.id) return undefined;

  const boundField = speakerControlBindings[control.id];
  if (boundField !== 'name' && boundField !== 'title') return undefined;

  const resolved =
    boundField === 'name' ? speakerValues.name : speakerValues.title;
  const hasValue = typeof resolved === 'string' && resolved.trim().length > 0;
  return hasValue ? NAMECARD_LOADING_PLACEHOLDER : '';
}
